import React, { useState, useEffect } from 'react';
import { useApp } from '../contexts/AppContext';
import { accountsAPI, unwrapVendors, unwrapBills } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import PeriodToolbar from '../components/PeriodToolbar';
import { usePeriod } from '../hooks/usePeriod';
import { PERIOD_MODES, businessToday } from '../lib/period';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { 
  FilePlus2, Search, Eye, Building2, Trash2,
  ChevronLeft, ChevronRight, Plus, FileDown, FileUp, Loader2
} from 'lucide-react';
import { toast } from 'sonner';

const formatCurrency = (value) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(value || 0);
};

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

const DocTypeBadge = ({ docType }) => {
  const isCredit = docType === 'CREDIT_NOTE';
  return (
    <Badge 
      variant="outline" 
      className={isCredit 
        ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' 
        : 'bg-rose-500/10 text-rose-400 border-rose-500/20'
      }
    >
      {isCredit ? 'Credit Note' : 'Debit Note'}
    </Badge>
  );
};

const CreditDebitNotesPage = () => {
  const { selectedOrg } = useApp();
  const [notes, setNotes] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [page, setPage] = useState(0);
  const periodState = usePeriod(PERIOD_MODES.RANGE_ALL_TIME);
  const [resolvedPeriod, setResolvedPeriod] = useState(null);
  const periodKey = JSON.stringify(periodState.apiParams);
  useEffect(() => { setPage(0); }, [periodKey]);
  const [selectedNote, setSelectedNote] = useState(null);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [vendors, setVendors] = useState([]);
  const [vendorMap, setVendorMap] = useState({});
  const [vendorBills, setVendorBills] = useState([]);
  const limit = 20;

  // Form state for creating new CN/DN
  const [formData, setFormData] = useState({
    vendor_id: '',
    doc_type: 'CREDIT_NOTE',
    note_number: '',
    note_date: businessToday(),
    amount: '',
    reason: '',
    notes: '',
    linked_bill_id: '',
  });

  useEffect(() => {
    if (selectedOrg) {
      loadVendors();
      loadNotes();
    }
  }, [selectedOrg, page, typeFilter, periodState.apiParams]);

  const loadVendors = async () => {
    try {
      const response = await accountsAPI.getVendors(selectedOrg.id);
      const list = unwrapVendors(response.data);
      setVendors(list);
      const map = {};
      list.forEach(v => {
        map[v.vendor_id || v.id] = v;
      });
      setVendorMap(map);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Operation failed");
      setVendors([]);
      setVendorMap({});
    }
  };

  // Load bills for selected vendor (for linking CN/DN to specific bills)
  const loadVendorBills = async (vendorId) => {
    if (!vendorId) {
      setVendorBills([]);
      return;
    }
    try {
      const response = await accountsAPI.getVendorBills(vendorId);
      setVendorBills(unwrapBills(response.data));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Operation failed");
      setVendorBills([]);
    }
  };

  const loadNotes = async () => {
    if (!selectedOrg) return;
    setLoading(true);
    try {
      const params = {
        skip: page * limit,
        limit,
      };
      if (typeFilter) params.doc_type = typeFilter;
      Object.assign(params, periodState.apiParams);

      const response = await accountsAPI.getCreditDebitNotes(params);
      setResolvedPeriod(response.data);
      setNotes(response.data.notes);
      setTotal(response.data.total);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Operation failed");
      toast.error('Failed to load credit/debit notes');
    }
    setLoading(false);
  };

  const handleCreate = async () => {
    if (!formData.vendor_id || !formData.amount) {
      toast.error('Please fill in required fields (Vendor and Amount)');
      return;
    }

    setCreating(true);
    try {
      await accountsAPI.createCreditDebitNote({
        ...formData,
        amount: parseFloat(formData.amount),
        linked_bill_id: formData.linked_bill_id || null,
      });
      toast.success(`${formData.doc_type === 'CREDIT_NOTE' ? 'Credit' : 'Debit'} Note created successfully`);
      setShowCreateDialog(false);
      setFormData({
        vendor_id: '',
        doc_type: 'CREDIT_NOTE',
        note_number: '',
        note_date: businessToday(),
        amount: '',
        reason: '',
        notes: '',
        linked_bill_id: '',
      });
      setVendorBills([]);
      loadNotes();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Operation failed");
      const errorMsg = err.response?.data?.detail || 'Failed to create note';
      toast.error(errorMsg);
    }
    setCreating(false);
  };

  const handleDelete = async (noteId) => {
    if (!confirm('Are you sure you want to delete this note?')) return;
    
    setDeleting(true);
    try {
      await accountsAPI.deleteCreditDebitNote(noteId);
      toast.success('Note deleted successfully');
      setSelectedNote(null);
      loadNotes();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Operation failed");
      const errorMsg = err.response?.data?.detail || 'Failed to delete note';
      toast.error(errorMsg);
    }
    setDeleting(false);
  };

  const filteredNotes = notes.filter(note => {
    if (!search) return true;
    const vendor = vendorMap[note.vendor_id];
    const searchLower = search.toLowerCase();
    return (
      note.note_number?.toLowerCase().includes(searchLower) ||
      vendor?.name?.toLowerCase().includes(searchLower) ||
      note.reason?.toLowerCase().includes(searchLower)
    );
  });

  const totalPages = Math.ceil(total / limit);

  // Calculate summary
  const creditTotal = notes.filter(n => n.doc_type === 'CREDIT_NOTE').reduce((sum, n) => sum + (n.amount || 0), 0);
  const debitTotal = notes.filter(n => n.doc_type === 'DEBIT_NOTE').reduce((sum, n) => sum + (n.amount || 0), 0);

  if (!selectedOrg) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-muted-foreground">Please select an organization</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="credit-debit-notes-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-manrope font-bold tracking-tight">Credit & Debit Notes</h1>
          <p className="text-muted-foreground mt-1">
            Manage vendor adjustments, returns, and corrections
          </p>
        </div>
        <Button 
          onClick={() => setShowCreateDialog(true)}
          className="bg-emerald-600 hover:bg-emerald-700 gap-2"
          data-testid="create-note-btn"
        >
          <Plus className="w-4 h-4" />
          Create Note
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-card/50 backdrop-blur-sm border-border/50">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Notes</p>
                <p className="text-2xl font-bold font-mono">{total}</p>
              </div>
              <FilePlus2 className="w-8 h-8 text-muted-foreground/50" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-emerald-500/5 border-emerald-500/20">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-emerald-400">Credit Notes</p>
                <p className="text-2xl font-bold font-mono text-emerald-400">{formatCurrency(creditTotal)}</p>
              </div>
              <FileDown className="w-8 h-8 text-emerald-400/50" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-rose-500/5 border-rose-500/20">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-rose-400">Debit Notes</p>
                <p className="text-2xl font-bold font-mono text-rose-400">{formatCurrency(debitTotal)}</p>
              </div>
              <FileUp className="w-8 h-8 text-rose-400/50" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="bg-card/50 backdrop-blur-sm border-border/50">
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search by note number, vendor, or reason..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10 bg-background/50"
                data-testid="note-search"
              />
            </div>
            <PeriodToolbar state={periodState} mode={PERIOD_MODES.RANGE_ALL_TIME} resolved={resolvedPeriod} />
            <div className="flex gap-2">
              {['', 'CREDIT_NOTE', 'DEBIT_NOTE'].map((type) => (
                <Button
                  key={type || 'all'}
                  variant={typeFilter === type ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => { setTypeFilter(type); setPage(0); }}
                  className={typeFilter === type ? 'bg-emerald-600 hover:bg-emerald-700' : ''}
                  data-testid={`filter-${type || 'all'}`}
                >
                  {type === '' ? 'All' : type === 'CREDIT_NOTE' ? 'Credit' : 'Debit'}
                </Button>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Notes Table */}
      <Card className="bg-card/50 backdrop-blur-sm border-border/50">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-4 space-y-4">
              {[...Array(5)].map((_, i) => (
                <Skeleton key={`skeleton-${i}`} className="h-16 w-full" />
              ))}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-border/50 hover:bg-transparent">
                  <TableHead className="font-manrope">Note Number</TableHead>
                  <TableHead className="font-manrope">Type</TableHead>
                  <TableHead className="font-manrope">Vendor</TableHead>
                  <TableHead className="font-manrope">Date</TableHead>
                  <TableHead className="font-manrope">Reason</TableHead>
                  <TableHead className="font-manrope text-right">Amount</TableHead>
                  <TableHead className="font-manrope w-[80px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredNotes.map((note) => (
                  <TableRow 
                    key={note.note_id} 
                    className="border-border/50 hover:bg-muted/50 cursor-pointer"
                    onClick={() => setSelectedNote(note)}
                    data-testid={`note-row-${note.note_id}`}
                  >
                    <TableCell className="font-mono text-sm">{note.note_number}</TableCell>
                    <TableCell><DocTypeBadge docType={note.doc_type} /></TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Building2 className="w-4 h-4 text-muted-foreground" />
                        {vendorMap[note.vendor_id]?.name || note.vendor_name || 'Unknown'}
                      </div>
                    </TableCell>
                    <TableCell className="font-mono text-sm">{formatDate(note.note_date)}</TableCell>
                    <TableCell className="max-w-[200px] truncate text-muted-foreground">
                      {note.reason || '-'}
                    </TableCell>
                    <TableCell className={`text-right font-mono font-medium ${
                      note.doc_type === 'CREDIT_NOTE' ? 'text-emerald-400' : 'text-rose-400'
                    }`}>
                      {note.doc_type === 'CREDIT_NOTE' ? '-' : '+'}{formatCurrency(note.amount)}
                    </TableCell>
                    <TableCell>
                      <Button variant="ghost" size="icon" className="h-8 w-8">
                        <Eye className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
                {filteredNotes.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                      No credit/debit notes found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Showing {page * limit + 1} to {Math.min((page + 1) * limit, total)} of {total}
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => Math.max(0, p - 1))}
              disabled={page === 0}
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
              disabled={page >= totalPages - 1}
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Note Detail Dialog */}
      <Dialog open={!!selectedNote} onOpenChange={() => setSelectedNote(null)}>
        <DialogContent className="max-w-lg bg-card border-border" data-testid="note-detail-dialog">
          <DialogHeader>
            <DialogTitle className="font-manrope flex items-center gap-3">
              {selectedNote?.doc_type === 'CREDIT_NOTE' ? 'Credit Note' : 'Debit Note'} Details
              {selectedNote && <DocTypeBadge docType={selectedNote.doc_type} />}
            </DialogTitle>
          </DialogHeader>
          {selectedNote && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Note Number</p>
                  <p className="font-mono font-medium">{selectedNote.note_number}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Date</p>
                  <p className="font-mono">{formatDate(selectedNote.note_date)}</p>
                </div>
                <div className="col-span-2">
                  <p className="text-sm text-muted-foreground">Vendor</p>
                  <p className="font-medium">{vendorMap[selectedNote.vendor_id]?.name || selectedNote.vendor_name || 'Unknown'}</p>
                </div>
              </div>

              <div className="border-t border-border/50 pt-4">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-sm text-muted-foreground">Amount</p>
                    <p className={`text-3xl font-mono font-bold ${
                      selectedNote.doc_type === 'CREDIT_NOTE' ? 'text-emerald-400' : 'text-rose-400'
                    }`}>
                      {selectedNote.doc_type === 'CREDIT_NOTE' ? '-' : '+'}{formatCurrency(selectedNote.amount)}
                    </p>
                  </div>
                </div>
              </div>

              {selectedNote.reason && (
                <div className="border-t border-border/50 pt-4">
                  <p className="text-sm text-muted-foreground mb-1">Reason</p>
                  <p className="text-sm">{selectedNote.reason}</p>
                </div>
              )}

              {selectedNote.notes && (
                <div className="border-t border-border/50 pt-4">
                  <p className="text-sm text-muted-foreground mb-1">Notes</p>
                  <p className="text-sm text-muted-foreground">{selectedNote.notes}</p>
                </div>
              )}

              {selectedNote.tax_amount > 0 && (
                <div className="border-t border-border/50 pt-4">
                  <p className="text-sm text-muted-foreground mb-1">Tax</p>
                  <p className="text-sm font-mono">
                    {selectedNote.tax_type}: {formatCurrency(selectedNote.tax_amount)}
                  </p>
                </div>
              )}

              <div className="border-t border-border/50 pt-4 flex justify-end">
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => handleDelete(selectedNote.note_id)}
                  disabled={deleting}
                  className="gap-2"
                >
                  {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                  Delete Note
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Create Note Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-lg bg-card border-border" data-testid="create-note-dialog">
          <DialogHeader>
            <DialogTitle className="font-manrope">Create Credit/Debit Note</DialogTitle>
            <DialogDescription>
              Credit notes reduce vendor outstanding. Debit notes increase it.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Note Type *</Label>
                <Select
                  value={formData.doc_type}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, doc_type: value }))}
                >
                  <SelectTrigger data-testid="select-doc-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="CREDIT_NOTE">Credit Note (reduces outstanding)</SelectItem>
                    <SelectItem value="DEBIT_NOTE">Debit Note (increases outstanding)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Date *</Label>
                <Input
                  type="date"
                  value={formData.note_date}
                  onChange={(e) => setFormData(prev => ({ ...prev, note_date: e.target.value }))}
                  data-testid="input-note-date"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Vendor *</Label>
              <Select
                value={formData.vendor_id}
                onValueChange={(value) => {
                  setFormData(prev => ({ ...prev, vendor_id: value, linked_bill_id: '' }));
                  loadVendorBills(value);
                }}
              >
                <SelectTrigger data-testid="select-vendor">
                  <SelectValue placeholder="Select vendor..." />
                </SelectTrigger>
                <SelectContent>
                  {vendors.map((vendor) => (
                    <SelectItem key={vendor.vendor_id || vendor.id} value={vendor.vendor_id || vendor.id}>
                      {vendor.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Optional: Link to a specific bill */}
            {formData.vendor_id && vendorBills.length > 0 && (
              <div className="space-y-2">
                <Label>Link to Bill (Optional)</Label>
                <Select
                  value={formData.linked_bill_id}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, linked_bill_id: value === "none" ? "" : value }))}
                >
                  <SelectTrigger data-testid="select-linked-bill">
                    <SelectValue placeholder="Select a bill to link (optional)..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No linked bill</SelectItem>
                    {vendorBills.map((bill) => (
                      <SelectItem key={bill.bill_id} value={bill.bill_id}>
                        {bill.bill_number} - {formatDate(bill.bill_date)} - {formatCurrency(bill.total_amount)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Linking helps track which bill this note relates to (e.g., goods returned)
                </p>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Note Number (auto-generated if blank)</Label>
                <Input
                  placeholder="CN-001 or DN-001"
                  value={formData.note_number}
                  onChange={(e) => setFormData(prev => ({ ...prev, note_number: e.target.value }))}
                  data-testid="input-note-number"
                />
              </div>
              <div className="space-y-2">
                <Label>Amount *</Label>
                <Input
                  type="number"
                  placeholder="0.00"
                  value={formData.amount}
                  onChange={(e) => setFormData(prev => ({ ...prev, amount: e.target.value }))}
                  data-testid="input-amount"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Reason</Label>
              <Select
                value={formData.reason}
                onValueChange={(value) => setFormData(prev => ({ ...prev, reason: value }))}
              >
                <SelectTrigger data-testid="select-reason">
                  <SelectValue placeholder="Select reason..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Goods returned">Goods returned</SelectItem>
                  <SelectItem value="Price difference">Price difference</SelectItem>
                  <SelectItem value="Quality issue">Quality issue</SelectItem>
                  <SelectItem value="Short delivery">Short delivery</SelectItem>
                  <SelectItem value="Duplicate payment">Duplicate payment</SelectItem>
                  <SelectItem value="Invoice error">Invoice error</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Additional Notes</Label>
              <Textarea
                placeholder="Any additional details..."
                value={formData.notes}
                onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                data-testid="input-notes"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleCreate}
              disabled={creating}
              className="bg-emerald-600 hover:bg-emerald-700 gap-2"
              data-testid="submit-create-note"
            >
              {creating && <Loader2 className="w-4 h-4 animate-spin" />}
              Create {formData.doc_type === 'CREDIT_NOTE' ? 'Credit' : 'Debit'} Note
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CreditDebitNotesPage;
